// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import XngCarousel, { CarouselStyle } from "@vanmeter/App/Components/XngCarousel/XngCarousel";
import { color, xScreen } from "@vanmeter/XDL/Utilities";

import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { navButtonCss } from "@vanmeter/App/Components/XngCarousel/styles";
import DataTable from "@vanmeter/MobiusOverrides/DataTable/DataTable";
import DataTableBody from "@vanmeter/MobiusOverrides/DataTable/DataTableBody";
import DataTableCell from "@vanmeter/MobiusOverrides/DataTable/DataTableCell";
import DataTableRow from "@vanmeter/MobiusOverrides/DataTable/DataTableRow";
import IconPaginationCarouselArrowLeft from "@vanmeter/XDL/Icons/IconPaginationCarouselArrowLeft";
import IconPaginationCarouselArrowRight from "@vanmeter/XDL/Icons/IconPaginationCarouselArrowRight";
import React, { createRef, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title: string;
    hideTitle?: boolean;
    maxNumberOfColumns: number;
    slideDetails: ProductCarouselSlideDetails;
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;

// XNG-Changes
type LeadCarousel = "main" | "attr";

export interface ProductCarouselSlideDetails {
    slides: CarouselSlide[];
    products: ProductModel[];
    attributeTypes: AttributeTypeModel[];
}

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselContentEmptyCellGridItem?: GridItemProps;
    carouselCellGridItem?: GridItemProps;
    carouselContentContainer?: GridContainerProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    attributeLabelsCellGridItem?: GridItemProps;
    attributeLabelsGridContainer?: GridContainerProps;
    attributeLabelGridItem?: GridItemProps;
    attributeLabelText?: TypographyPresentationProps;
    attributeValuesCellGridItem?: GridItemProps;
    attributeValuesWrapper?: InjectableCss;
    attributeValuesContainer?: InjectableCss;
    attributeValuesSlidesContainer?: InjectableCss;
    attributeValueSlide?: InjectableCss;
    attributeValueSlideInner?: InjectableCss;
    attributeValuesGridContainer?: GridContainerProps;
    attributeValueGridItem?: GridItemProps;
    attributeValueText?: TypographyPresentationProps;
    // XNG-Changes
    xngCarouselStyles?: CarouselStyle;
    Wrapper?: InjectableCss;
    cellStyles?: DataTableCellProps;
}

export const carouselStyles: CarouselStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        css: css`
            justify-content: flex-end;
            padding-top: 230px;
            opacity: 0.5;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "common.ui6",
        css: css`
            margin-right: 45px;
            &:disabled {
                visibility: hidden;
            }

            ${xScreen("lte", "sm")} {
                margin-right: 15px;
            }
            ${xScreen("lte", "xs")} {
                display: none;
            }
            border: 0;
            padding: 0;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        css: css`
            justify-content: flex-start;
            padding-top: 230px;
            opacity: 0.5;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "common.ui6",
        css: css`
            &:disabled {
                visibility: hidden;
            }
            ${xScreen("lte", "xs")} {
                display: none;
            }
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: {
        width: 8,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
        `,
    },
    carouselContentContainer: {
        gap: 0,
    },

    carouselCellGridItem: {
        width: 12,
        css: css`
            padding-bottom: 62px;
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            display: flex;
            justify-content: center;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    attributeLabelsCellGridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding: 0 45px;
        `,
    },

    attributeLabelGridItem: {
        width: 12,
        css: css`
            height: 60px;
            ${({ attrIndex }: any) => `padding: ${attrIndex === 0 ? `0 16px 16px 0` : `24px 16px 16px 0`}`};
        `,
    },
    attributeLabelText: {
        ellipsis: true,
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    attributeValuesCellGridItem: {
        width: 12,
    },
    attributeValuesWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    attributeValuesContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    attributeValuesSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    attributeValueSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    attributeValueSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0;
            display: flex;
            flex-direction: column;
        `,
    },
    attributeValuesGridContainer: {
        gap: 0,
    },
    attributeValueGridItem: {
        width: 12,
        css: css`
            &:nth-child(4n + 2) {
                background: ${color("common.ui11")};
                border: none;
            }
            padding: 12px;
            border: 1px solid ${color("common.ui4")};
            ${xScreen("gte", "md")} {
                ${({ rightBorders, leftBorders }: any) => `
                    ${rightBorders ? "" : "border-right: none;"}
                    ${leftBorders ? "" : "border-left: none;"}
                `}
            }
        `,
    },
    attributeValueText: {
        ellipsis: true,
    },
};
// XNG-Changes: change structure & styles
const Carousel = ({
    shellContext,
    theme,
    id,
    extendedStyles,
    title,
    hideTitle,
    maxNumberOfColumns,
    slideDetails,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    // XNG-changes
    const rootRef = useRef<HTMLDivElement>(null);
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    const mainCarouselApiRef = useRef<any>(null);
    const attributesCarouselApiRef = useRef<any>(null);
    const leadCarouselRef = useRef<LeadCarousel | null>(null);
    const [bothApisReady, setBothApisReady] = useState(false);

    const [windowResizeTime, setWindowResizeTime] = useState(0);

    // XNG-changes:
    const setCanScroll = useCallback(() => {
        const api = leadCarouselRef.current === "attr" ? attributesCarouselApiRef.current : mainCarouselApiRef.current;
        const root = rootRef.current;
        if (shellContext.isInShell || !api || !root) {
            return;
        }
        const prevBtn = root.querySelector<HTMLButtonElement>(".prev-btn");
        const nextBtn = root.querySelector<HTMLButtonElement>(".next-btn");
        if (prevBtn) prevBtn.disabled = !api.canScrollPrev();
        if (nextBtn) nextBtn.disabled = !api.canScrollNext();
    }, [shellContext.isInShell]);

    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 2;
        } else {
            localSlidesToScroll = 3;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(0);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);

    // XNG-changes
    useEffect(() => {
        if (mainCarouselApiRef.current && attributesCarouselApiRef.current && !bothApisReady) {
            setBothApisReady(true);
        }
    }, [bothApisReady]);

    const scrollPrev = useCallback(() => {
        mainCarouselApiRef.current?.scrollPrev();
    }, []);

    const scrollNext = useCallback(() => {
        mainCarouselApiRef.current?.scrollNext();
    }, []);

    // XNG-changes
    useEffect(() => {
        if (!bothApisReady) return;
        const mainApi = mainCarouselApiRef.current;
        const attrApi = attributesCarouselApiRef.current;

        if (!mainApi || !attrApi) return;

        const leadCarousel = leadCarouselRef;

        const syncCarousles = (firstCarousel: any, secondCarousel: any) => {
            const firstCarouselEngine = firstCarousel.internalEngine();
            const secondCarouselEngine = secondCarousel.internalEngine();

            // XNG-changes: Map the first carousel's scroll progress to the second carousel's position
            const startPosition = firstCarouselEngine.limit.min - firstCarouselEngine.limit.max;
            const progress =
                startPosition === 0
                    ? 0
                    : (firstCarouselEngine.offsetLocation.get() - firstCarouselEngine.limit.max) / startPosition;
            const endPosition = secondCarouselEngine.limit.min - secondCarouselEngine.limit.max;
            const target = secondCarouselEngine.limit.max + progress * endPosition;

            secondCarouselEngine.location.set(target);
            secondCarouselEngine.previousLocation.set(target);
            secondCarouselEngine.offsetLocation.set(target);
            secondCarouselEngine.target.set(target);
            secondCarouselEngine.translate.to(target);
            secondCarouselEngine.index.set(firstCarouselEngine.index.get());
        };

        const onMainPointerDown = () => {
            leadCarousel.current = "main";
        };
        const onAttrPointerDown = () => {
            leadCarousel.current = "attr";
        };

        const onMainScroll = () => {
            if (leadCarousel.current === "attr") return;
            syncCarousles(mainApi, attrApi);
        };
        const onAttrScroll = () => {
            if (leadCarousel.current !== "attr") return;
            syncCarousles(attrApi, mainApi);
        };

        const onMainSettle = () => {
            if (leadCarousel.current === "main") leadCarousel.current = null;
            setCanScroll();
        };
        const onAttrSettle = () => {
            if (leadCarousel.current === "attr") leadCarousel.current = null;
            setCanScroll();
        };

        mainApi.on("pointerDown", onMainPointerDown);
        attrApi.on("pointerDown", onAttrPointerDown);
        mainApi.on("scroll", onMainScroll);
        attrApi.on("scroll", onAttrScroll);
        mainApi.on("settle", onMainSettle);
        attrApi.on("settle", onAttrSettle);
        mainApi.on("select", setCanScroll);
        attrApi.on("select", setCanScroll);
        mainApi.on("reInit", setCanScroll);
        setCanScroll();

        return () => {
            mainApi.off("pointerDown", onMainPointerDown);
            attrApi.off("pointerDown", onAttrPointerDown);
            mainApi.off("scroll", onMainScroll);
            attrApi.off("scroll", onAttrScroll);
            mainApi.off("settle", onMainSettle);
            attrApi.off("settle", onAttrSettle);
            mainApi.off("select", setCanScroll);
            attrApi.off("select", setCanScroll);
            mainApi.off("reInit", setCanScroll);
        };
    }, [bothApisReady, setCanScroll]);

    const getProductAttributeValue = (product: ProductModel, attribute: AttributeTypeModel) => {
        const productAttributeValue = product.attributeTypes
            ?.filter(a => a.id === attribute.id)
            .map(a => a.attributeValues || [])
            .reduce((acc, currentValue) => [...acc, ...currentValue], []);

        if (productAttributeValue && productAttributeValue.length > 0) {
            return (
                <Typography
                    aria-describedby={`carousel-${id}_attribute-label-${attribute.id}`}
                    className="attribute"
                    {...styles.attributeValueText}
                >
                    {productAttributeValue.map(o => o.valueDisplay).join(", ")}
                </Typography>
            );
        }

        return (
            <Typography {...styles.attributeValueText} className="attribute">
                --
            </Typography>
        );
    };

    if (!slideDetails || slideDetails.slides.length === 0) {
        return null;
    }

    // XNG-Changes:
    const displayArrows = slideDetails.slides.length > 3;

    return (
        <StyledWrapper {...styles.Wrapper} ref={rootRef} data-test-selector="omartest">
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            {hideTitle ? (
                <VisuallyHidden>
                    <Typography {...styles.titleText}>{title}</Typography>
                </VisuallyHidden>
            ) : (
                <Typography {...styles.titleText}>{title}</Typography>
            )}

            <GridContainer {...styles.mainContainer} data-test-selector="productCompareCarousel">
                <GridItem {...styles.prevArrowGridItem}>
                    {displayArrows && (
                        <Button
                            {...styles.prevArrowButton}
                            {...navButtonCss("left")}
                            onClick={scrollPrev}
                            disabled={false}
                            data-test-selector="prevBtn"
                            aria-label="Scroll back"
                            className="prev-btn"
                        >
                            <ButtonIcon src={IconPaginationCarouselArrowLeft} size={56} />
                        </Button>
                    )}
                </GridItem>

                <GridItem {...styles.carouselGridItem}>
                    <GridContainer {...styles.carouselContentContainer}>
                        <GridItem {...styles.carouselContentEmptyCellGridItem}></GridItem>
                        <GridItem {...styles.carouselCellGridItem}>
                            <StyledWrapper {...{ ...styles.carouselWrapper, slidesToScroll }}>
                                <XngCarousel
                                    navigation={false}
                                    slidesPerView={slidesToScroll}
                                    spaceBetween={0}
                                    subGridRows={10}
                                    options={{
                                        align: "start",
                                        slidesToScroll: 1,
                                        loop: false,
                                        dragFree: false,
                                    }}
                                    updateOnWindowResize={true}
                                    onInit={api => {
                                        mainCarouselApiRef.current = api;
                                        if (attributesCarouselApiRef.current) {
                                            setBothApisReady(true);
                                        }
                                    }}
                                    extendedStyles={styles.xngCarouselStyles}
                                >
                                    {slideDetails.slides.map(slide => (
                                        <StyledWrapper
                                            key={slide.id}
                                            {...styles.carouselSlideInner}
                                            id={`slide_${slide.id}`}
                                            data-test-key={slide.id}
                                            data-test-selector="carousel_slide"
                                        >
                                            {slide.renderComponent({})}
                                        </StyledWrapper>
                                    ))}
                                </XngCarousel>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.attributeLabelsCellGridItem} data-attribute-labels>
                            {/* XNG-Change */}
                            <DataTable>
                                <DataTableBody>
                                    {slideDetails.attributeTypes.map(attribute => (
                                        <DataTableRow key={attribute.id}>
                                            <DataTableCell {...styles.cellStyles}>
                                                <Typography
                                                    id={`carousel-${id}_attribute-label-${attribute.id}`}
                                                    className="attribute"
                                                    {...styles.attributeLabelText}
                                                >
                                                    {attribute.label || attribute.name}
                                                </Typography>
                                            </DataTableCell>
                                        </DataTableRow>
                                    ))}
                                </DataTableBody>
                            </DataTable>
                        </GridItem>
                        <GridItem {...styles.attributeValuesCellGridItem}>
                            <StyledWrapper {...{ ...styles.attributeValuesWrapper, slidesToScroll }}>
                                <XngCarousel
                                    navigation={false}
                                    slidesPerView={slidesToScroll}
                                    spaceBetween={0}
                                    options={{
                                        align: "start",
                                        slidesToScroll: 1,
                                        loop: false,
                                        dragFree: false,
                                    }}
                                    updateOnWindowResize={true}
                                    onInit={api => {
                                        attributesCarouselApiRef.current = api;
                                        if (mainCarouselApiRef.current) {
                                            setBothApisReady(true);
                                        }
                                    }}
                                >
                                    {slideDetails.products.map(product => (
                                        <StyledWrapper
                                            key={`${product.id}-attributes`}
                                            {...styles.attributeValueSlideInner}
                                            id={`product-attribute_${product.id}`}
                                            data-test-selector="slideAttributesContainer"
                                            data-test-key={product.id}
                                        >
                                            {/* XNG-Change */}
                                            <DataTable>
                                                <DataTableBody>
                                                    {slideDetails.attributeTypes.map(attribute => (
                                                        <DataTableRow key={`${product.id}-attribute-${attribute.id}`}>
                                                            <DataTableCell {...styles.cellStyles}>
                                                                {getProductAttributeValue(product, attribute)}
                                                            </DataTableCell>
                                                        </DataTableRow>
                                                    ))}
                                                </DataTableBody>
                                            </DataTable>
                                        </StyledWrapper>
                                    ))}
                                </XngCarousel>
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.nextArrowGridItem}>
                    {displayArrows && (
                        <Button
                            {...styles.nextArrowButton}
                            {...navButtonCss("right")}
                            disabled={false}
                            onClick={scrollNext}
                            data-test-selector="nextBtn"
                            className="next-btn"
                            aria-label="Scroll next"
                        >
                            <ButtonIcon src={IconPaginationCarouselArrowRight} size={56} />
                        </Button>
                    )}
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </StyledWrapper>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default withIsInShell(withTheme(Carousel));
